<script lang="ts">
    import { getContext, onDestroy, onMount } from "svelte";
    import type { Writable } from "svelte/store";
    import { Routes } from "@lib/types";
    import { formatTime, difficultyGroupName, PuzzleDate, percentileBand } from "@lib/util";
    import type { Attempt, Puzzle, Recap, Stats } from "@lib/types";
    import { db } from "@lib/db";
    import { recap as fetchRecap } from "@lib/api";
    import ResetTimer from "@components/ResetTimer.svelte";
    import Logo from "@components/Logo.svelte";
    import Summary from "@components/Summary.svelte";
    import ShareIcon from "@icons/Share.svelte";

    let router = getContext<Writable<Routes>>("router");
    let date = getContext<Writable<PuzzleDate>>("date");

    let puzzle: Puzzle | undefined = $state(undefined);
    let attempt: Attempt | undefined = $state(undefined);
    let showAdmin = $state(false);
    let solveStats: Stats | undefined = $state(undefined);
    let recapData: Recap | undefined = $state(undefined);
    let recapDismissed = $state(false);

    const unsubscribe = date.subscribe(async (newDate: PuzzleDate) => {
        try {
            puzzle = await db.puzzles.get({ date: newDate.iso });
            if (puzzle) {
                attempt = await db.attempts.get({ puzzle_id: puzzle.id });
            } else {
                attempt = undefined;
            }
        } catch (e) {
            // noop
        }

        recapData = undefined;
        recapDismissed = false;
        loadRecap(newDate);
    });

    const loadRecap = async (currentDate: PuzzleDate) => {
        try {
            const d = currentDate.date;
            const yesterday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - 1));
            const yesterdayIso = yesterday.toISOString().split("T")[0];

            const yesterdayPuzzle = await db.puzzles.get({ date: yesterdayIso });
            if (!yesterdayPuzzle) return;

            const yesterdayAttempt = await db.attempts.get({ puzzle_id: yesterdayPuzzle.id });
            if (!yesterdayAttempt?.solved || !yesterdayAttempt.session) return;

            const data = await fetchRecap(yesterdayPuzzle.id, yesterdayAttempt.session);
            if (data && data.percentile > 0) {
                recapData = data;
            }
        } catch (e) {
            // noop — recap is best-effort
        }
    };

    const goToArchive = () => {
        router.set(Routes.Archive);
        window.history.pushState({ page: Routes.Archive, date: $date }, "Archive", "");
    };

    const goToPuzzle = () => {
        router.set(Routes.Puzzle);
        window.history.pushState({ page: Routes.Puzzle, date: $date }, "Puzzle", "");
    };

    const goToAdmin = () => {
        router.set(Routes.AdminPuzzles);
        window.history.pushState({ page: Routes.AdminPuzzles, date: $date }, "Admin", "");
    };

    onMount(() => {
        try {
            showAdmin = localStorage.getItem("twokings_admin") === "1";
        } catch (e) {
            showAdmin = false;
        }
    });

    const onStatsLoaded = (stats: Stats) => {
        solveStats = stats;
    };

    const shareMessage = (): string => {
        if (!puzzle || !attempt?.solved) return "";
        const time = formatTime(solveStats?.solve_time ?? attempt.timer.elapsed);
        const pct = solveStats?.percentile ?? 0;
        if (pct > 0) {
            return `I solved Two Kings #${puzzle.id} in ${time} — ${percentileBand(pct)} of solvers 👑👑`;
        }
        return `I solved Two Kings #${puzzle.id} in ${time}. 👑👑`;
    };

    const onShareClick = async (e: any) => {
        const message = shareMessage();

        if (navigator.share) {
            try {
                await navigator.share({
                    title: "Two Kings",
                    text: message,
                    url: "https://twokings.xyz",
                });
            } catch (e) {
                // noop
            }
        } else {
            navigator.clipboard.writeText(`${message}\nhttps://twokings.xyz`);
            e.target.textContent = "Copied!";
            setTimeout(() => {
                e.target.textContent = "Share your time";
            }, 2000);
        }
    };

    onDestroy(unsubscribe);
</script>

<ResetTimer />

<div class="container">
    <main>
        <header>
            <Logo />
            <h4>TWO KINGS</h4>
        </header>

        <section>
            <div>
                <h1 class="fade-in">
                    {$date.date.toLocaleDateString("en-us", {
                        weekday: "long",
                    })}
                </h1>
                <h4 class="fade-in">{$date.friendly}</h4>
            </div>

            {#if puzzle && attempt?.solved}
                <div>
                    <Summary {puzzle} {attempt} onStats={onStatsLoaded} />

                    <button class="link fade-in" onclick={goToPuzzle}>
                        Admire solution →
                    </button>
                </div>

                <button class="button fade-in" onclick={onShareClick}>
                    {#if typeof navigator !== "undefined" && typeof navigator.share === "function"}
                        <div class="button-icon">
                            <ShareIcon />
                        </div>
                    {/if}
                    Share your time
                </button>

                <button class="archive-cta fade-in" onclick={goToArchive}>
                    <div>Looking for more puzzles?</div>
                    <div><b>Explore the archive →</b></div>
                </button>
            {:else}
                {#if recapData && !recapDismissed}
                    <div class="recap-card oswald fade-in">
                        <p class="recap-intro">Yesterday's results are in</p>
                        <p class="recap-headline monospace">{percentileBand(recapData.percentile)}</p>
                        <p class="recap-detail">
                            You solved #{recapData.puzzle_id}
                            ({difficultyGroupName(recapData.difficulty)})
                            in {formatTime(recapData.solve_time)}
                        </p>
                        <button class="recap-dismiss" onclick={() => recapDismissed = true}>✕</button>
                    </div>
                {/if}

                <button class="button fade-in" onclick={goToPuzzle}>
                    {#if attempt}
                        Continue
                    {:else}
                        Start
                    {/if}
                </button>
                <button class="link fade-in" onclick={goToArchive}>
                    Archive →
                </button>

                {#if showAdmin}
                    <button class="link fade-in" onclick={goToAdmin}>
                        Admin →
                    </button>
                {/if}
            {/if}
        </section>

        <footer></footer>
    </main>
</div>

<style>
    .archive-cta {
        border: 1px solid rgba(0, 0, 0, 0.05);
        border-radius: 8px;
        background-color: rgba(0, 0, 0, 0.05);
        color: var(--black);
        padding: 1rem;
        width: 100%;
        cursor: pointer;
    }

    .recap-card {
        position: relative;
        width: 272px;
        text-align: center;
        padding: 1rem 1.25rem;
        border: 2px solid var(--black);
        border-radius: 8px;
        background-color: rgba(255, 255, 255, 0.35);
    }

    .recap-intro {
        font-size: 0.8rem;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        margin-bottom: 0.25rem;
    }

    .recap-headline {
        font-size: 2rem;
        line-height: 1.1;
        margin-bottom: 0.25rem;
    }

    .recap-detail {
        font-size: 0.8rem;
        opacity: 0.8;
    }

    .recap-dismiss {
        position: absolute;
        top: 0.35rem;
        right: 0.5rem;
        background: none;
        border: 0;
        cursor: pointer;
        font-size: 0.85rem;
        opacity: 0.5;
        color: var(--black);
    }

    .container {
        background-color: var(--accent-color);
        color: var(--black);
        animation: 0.25s fade ease;
        padding: 0 2rem;
    }

    button.link {
        color: var(--black);
    }

    main {
        display: flex;
        flex-direction: column;
        align-items: center;
        min-height: 100vh;
    }

    header {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 3rem 0;
    }

    section {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2rem;
        text-align: center;
        padding: 2rem 0;
    }

    footer {
        flex-grow: 1;
    }
</style>
